import axios, { AxiosResponse } from 'axios'

import { NotificationCenterHeaderName, NotificationCenterLog } from '@/entities/NotificationCenter/NotificationCenter'
import { TransportationOption } from '@/entities/TransportationOption/TransportationOption'

const includes = [
    'person',
    'roles',
    'roles.permissions',
    'person.emergencyContact',
    'person.addresses',
    'departments',
    'transportationOptions',
]

export const getProfileOfCurrentUser = (): Promise<AxiosResponse> => axios.get(
    `${process.env.REACT_APP_API_URL}/me/profile`,
    {
        params: {
            include: includes.join(','),
        },
    },
)

export const generatePersonGenderData = (personId: string, gender: string) => ({
    type: 'people',
    id: personId,
    attributes: {
        gender,
    },
})

export const patchUserPhoneAndEmail = (
    userId: string,
    phone: string | null,
    email: string | null,
    eventLogTrackerData?: NotificationCenterLog,
): Promise<AxiosResponse> => {
    const body = {
        type: 'users',
        id: userId,
        attributes: {
            ...(phone && { phone }),
            ...(email && { email }),
        },
    }

    return axios.patch(
        `${process.env.REACT_APP_API_URL}/me/user`,
        { data: body },
        {
            params: {
                include: includes.join(','),
            },
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        },
    )
}

export const patchPersonGender = (personId: string, gender: string, eventLogTrackerData?: NotificationCenterLog): Promise<AxiosResponse> => {
    const body = generatePersonGenderData(personId, gender)
    return axios.patch(`${process.env.REACT_APP_API_URL}/people/${personId}`, { data: body }, {
        headers: {
            [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
        },
    })
}

export const postUserAddress = (
    personId: string,
    street: string,
    houseNumber: string,
    houseNumberAddition: string,
    postalCode: string,
    city: string,
    eventLogTrackerData?: NotificationCenterLog,
): Promise<AxiosResponse> => {
    const body = {
        type: 'addresses',
        id: null,
        attributes: {
            street,
            houseNumber,
            houseNumberAddition,
            postalCode,
            city,
        },
    }

    return axios.post(`${process.env.REACT_APP_API_URL}/me/address`, { data: body }, {
        headers: {
            [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
        },
    })
}

const generateEmergencyBodyData = (
    personId: string,
    emergencyContactId: string | undefined,
    fullName: string,
    phone: string,
    relationType: string,
) => {
    return {
        type: 'emergencyContacts',
        id: emergencyContactId,
        attributes: {
            fullName: fullName || '',
            relationType: relationType || '',
            phone: phone || '',
        },
        relationships: {
            person: {
                data: {
                    type: 'people',
                    id: personId,
                },
            },
        },
    }
}

export const patchEmergencyContact = (
    personId: string,
    emergencyContactId: string | undefined,
    fullName: string,
    phone: string,
    relationType: string,
    eventLogTrackerData?: NotificationCenterLog,
): Promise<AxiosResponse> => {
    const body = generateEmergencyBodyData(
        personId, emergencyContactId, fullName, phone, relationType,
    )
    return axios.put(`${process.env.REACT_APP_API_URL}/me/emergency-contacts`, { data: body }, {
        headers: {
            [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
        },
    })
}

const generateTransportationOptionsBodyData = (personId: string, transportationOptions: TransportationOption[]) => {
    return transportationOptions.map((transportationOption) => ({
        type: 'transportationOptions',
        id: transportationOption.id,
        attributes: {
            name: transportationOption.name,
        },
        relationships: {
            person: {
                data: {
                    type: 'people',
                    id: personId,
                },
            },
        },
    }))
}

export const patchUserTransportationOptions = (
    personId: string,
    transportationOptions: TransportationOption[],
    eventLogTrackerData?: NotificationCenterLog,
): Promise<AxiosResponse> => {
    const body = generateTransportationOptionsBodyData(personId, transportationOptions)
    return axios.put(`${process.env.REACT_APP_API_URL}/me/transportation-options`, { data: body }, {
        headers: {
            [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
        },
    })
}
